<template>
  <transition name="redeem-fade">
    <div v-if="open" class="redeem-mask" @click.self="close">
      <transition name="redeem-slide">
        <aside v-if="open" class="redeem-panel" role="dialog" aria-modal="true">
          <!-- 专业资产管理终端背景 -->
          <div class="redeem-grid-bg"></div>
          <div class="redeem-scan-beam"></div>
          <div class="redeem-aurora redeem-aurora-cyan"></div>
          <div class="redeem-aurora redeem-aurora-violet"></div>
          <div class="redeem-particles" aria-hidden="true">
            <i></i>
            <i></i>
            <i></i>
            <i></i>
          </div>
          <header class="redeem-header">
            <div class="header-copy">
              <span>ASSET RELEASE & SETTLEMENT</span>
              <h2>{{ isTrial ? '体验策略到期结算' : '标准策略额度释放' }}</h2>
            </div>
            <button type="button" class="close-btn" @click="close">×</button>
          </header>

          <div v-if="order" class="redeem-body">
            <section :class="['order-summary', isTrial ? 'trial' : 'standard']">
              <div class="summary-glow"></div>
              <div class="summary-top">
                <div>
                  <span :class="['type-badge', isTrial ? 'trial' : 'standard']">
                    {{ isTrial ? '七天体验策略' : '标准执行策略' }}
                  </span>
                  <h3>{{ order.orderNo || '--' }}</h3>
                  <p>{{ order.createTime || '--' }}</p>
                </div>
                <span :class="['status-badge', canSubmit ? 'ready' : 'disabled']">
                  <i></i>{{ actionStatusText }}
                </span>
              </div>

              <div v-if="!isTrial" class="summary-assets">
                <div>
                  <span>执行中额度</span>
                  <strong>{{ money(order.earningPrincipalUsdt) }}</strong>
                  <em>USDT</em>
                </div>
                <div>
                  <span>本次释放额度</span>
                  <strong>{{ money(redeemPrincipalUsdt) }}</strong>
                  <em>{{ redeemUnitPercent }}% / 次</em>
                </div>
              </div>

              <div v-else class="summary-assets">
                <div>
                  <span>体验配置额度</span>
                  <strong>{{ money(order.depositAmountUsdt) }}</strong>
                  <em>USDT</em>
                </div>
                <div>
                  <span>预设结算结果</span>
                  <strong class="positive">+{{ money(trialProfitUsdt) }}</strong>
                  <em>USDT</em>
                </div>
              </div>
            </section>

            <!-- 标准策略额度释放 -->
            <template v-if="!isTrial">
              <section class="redeem-section">
                <div class="section-title">
                  <div>
                    <span>RELEASE SCHEDULE</span>
                    <h3>选择额度释放周期</h3>
                  </div>
                  <b>STEP {{ nextRedeemStep }}</b>
                </div>

                <div class="period-grid">
                  <button
                    v-for="option in normalizedOptions"
                    :key="option.days"
                    type="button"
                    :class="['period-card', { active: selectedDays === option.days }]"
                    @click="selectedDays = option.days"
                  >
                    <span class="period-check"></span>
                    <div>
                      <strong>{{ option.days }} 天</strong>
                      <small>{{ option.label || '额度释放' }}</small>
                    </div>
                    <em>{{ percent(option.feeRate) }} 释放服务费</em>
                  </button>
                </div>

                <div class="release-notice">
                  <span>!</span>
                  <p>
                    提交后，本次释放的 {{ money(redeemPrincipalUsdt) }} USDT 将立即从执行中额度中扣除，
                    并进入 {{ selectedDays }} 天额度释放。释放期间该部分额度不再参与策略执行与后续结算。
                  </p>
                </div>
              </section>

              <section class="redeem-section">
                <div class="section-title">
                  <div>
                    <span>RELEASE ESTIMATE</span>
                    <h3>本次释放测算</h3>
                  </div>
                  <b>BTC ESTIMATE</b>
                </div>

                <div class="calc-list">
                  <div>
                    <span>本次释放额度</span>
                    <strong>{{ money(redeemPrincipalUsdt) }} USDT</strong>
                  </div>
                  <div>
                    <span>{{ selectedDays }} 天释放释放服务费</span>
                    <strong class="negative">-{{ money(redeemFeeUsdt) }} USDT</strong>
                  </div>
                  <div>
                    <span>预计净结算金额</span>
                    <strong>{{ money(netRedeemUsdt) }} USDT</strong>
                  </div>
                  <div>
                    <span>预计每日释放额度</span>
                    <strong>{{ money(dailyNetReleaseUsdt) }} USDT</strong>
                  </div>
                  <div>
                    <span>BTC 参考价格</span>
                    <strong>{{ money(btcPriceUsdt) }} USDT</strong>
                  </div>
                  <div class="btc-result">
                    <span>预计结算 BTC</span>
                    <strong>≈ {{ btc(estimatedBtc) }} BTC</strong>
                  </div>
                </div>

                <p class="price-tip">
                  BTC 数量仅基于当前参考价格测算，实际结算数量以每次额度释放时的 BTC/USDT 市场价格为准。
                </p>
              </section>

              <section class="redeem-section">
                <div class="section-title">
                  <div>
                    <span>ALLOCATION STATUS</span>
                    <h3>提交后的执行额度变化</h3>
                  </div>
                </div>

                <div class="principal-change">
                  <div>
                    <span>提交前执行中额度</span>
                    <strong>{{ money(order.earningPrincipalUsdt) }}</strong>
                    <em>USDT</em>
                  </div>
                  <div class="arrow">→</div>
                  <div class="after">
                    <span>提交后执行中额度</span>
                    <strong>{{ money(remainingEarningPrincipal) }}</strong>
                    <em>USDT</em>
                  </div>
                </div>
              </section>
            </template>

            <!-- 七天体验策略提取 -->
            <template v-else>
              <section class="redeem-section trial-section">
                <div class="section-title">
                  <div>
                    <span>MATURITY SETTLEMENT</span>
                    <h3>体验策略到期结算</h3>
                  </div>
                  <b>U IN · U OUT</b>
                </div>

                <div class="trial-timeline">
                  <div class="timeline-item done">
                    <i>✓</i>
                    <span><b>策略启用</b><small>{{ order.startDate || '--' }}</small></span>
                  </div>
                  <div class="timeline-line"></div>
                  <div :class="['timeline-item', { done: trialMatured }]">
                    <i>{{ trialMatured ? '✓' : '2' }}</i>
                    <span><b>周期完成</b><small>{{ order.maturityDate || '--' }}</small></span>
                  </div>
                  <div class="timeline-line"></div>
                  <div :class="['timeline-item', { done: trialMatured }]">
                    <i>{{ trialMatured ? '✓' : '3' }}</i>
                    <span><b>到期统一结算</b><small>USDT 结算</small></span>
                  </div>
                </div>

                <div class="trial-settlement-card">
                  <div>
                    <span>体验配置额度</span>
                    <strong>{{ money(order.depositAmountUsdt) }}</strong>
                    <em>USDT</em>
                  </div>
                  <div class="plus">+</div>
                  <div>
                    <span>固定收益</span>
                    <strong>{{ money(trialProfitUsdt) }}</strong>
                    <em>USDT</em>
                  </div>
                  <div class="equal">=</div>
                  <div class="total">
                    <span>本次结算总额</span>
                    <strong>{{ money(trialClaimUsdt) }}</strong>
                    <em>USDT</em>
                  </div>
                </div>

                <div :class="['maturity-note', trialMatured ? 'ready' : 'waiting']">
                  <span>{{ trialMatured ? '✓' : 'i' }}</span>
                  <p>{{ trialMaturityText }}</p>
                </div>
              </section>

              <section class="redeem-section">
                <div class="section-title">
                  <div>
                    <span>EXPERIENCE STRATEGY RULES</span>
                    <h3>体验策略结算规则</h3>
                  </div>
                </div>

                <div class="rule-list">
                  <div><i>01</i><p><b>固定周期</b><span>策略启用后运行七天，周期完成前不支持提前释放。</span></p></div>
                  <div><i>02</i><p><b>固定收益</b><span>周期完成后按照策略记录的预设结算结果执行。</span></p></div>
                  <div><i>03</i><p><b>USDT 结算</b><span>体验策略使用 USDT 配置，并以 USDT 完成到期统一结算。</span></p></div>
                  <div><i>04</i><p><b>独立规则</b><span>不纳入合作网络配置规模，也不产生协作激励。</span></p></div>
                </div>
              </section>
            </template>

            <label class="confirm-row">
              <input v-model="confirmed" type="checkbox" />
              <span class="fake-check">✓</span>
              <p v-if="isTrial">
                我已核对该体验策略的到期结算数据，并确认提交全部配置额度及结算结果。
              </p>
              <p v-else>
                我已确认本次本金提交后立即停止计息，并按照所选周期额度释放及结算。
              </p>
            </label>
          </div>

          <footer v-if="order" class="redeem-footer">
            <button type="button" class="cancel-btn" :disabled="submitting" @click="close">
              返回检查
            </button>
            <button
              type="button"
              :class="['confirm-btn', isTrial ? 'trial' : 'standard']"
              :disabled="!canSubmit || !confirmed || submitting"
              @click="submit"
            >
              <span v-if="submitting" class="loading-dot"></span>
              {{ submitButtonText }}
            </button>
          </footer>
        </aside>
      </transition>
    </div>
  </transition>
</template>

<script>
import {
applyBchPrincipalRedeem,
claimBchTrialOrder
} from '@/api/bchArbitrage'

export default {
name: 'BchOrderRedeemDrawer',
props: {
  open: {
    type: Boolean,
    default: false
  },
  order: {
    type: Object,
    default: null
  },
  btcPriceUsdt: {
    type: [Number, String],
    default: 0
  },
  redeemOptions: {
    type: Array,
    default() {
      return [
        { days: 7, label: '快速额度释放', feeRate: 0.1 },
        { days: 30, label: '标准额度释放', feeRate: 0 }
      ]
    }
  },
  /*
   * 保留这个属性是为了兼容你原来父页面的写法。
   * 组件现在会直接调用 BCH 赎回接口，父页面无需再负责发请求。
   */
  loading: {
    type: Boolean,
    default: false
  }
},
data() {
  return {
    selectedDays: 7,
    confirmed: false,
    internalLoading: false
  }
},
computed: {
  submitting() {
    return this.loading || this.internalLoading
  },
  isTrial() {
    return Number(this.order && this.order.orderType) === 2
  },
  normalizedOptions() {
    const source = Array.isArray(this.redeemOptions) && this.redeemOptions.length
      ? this.redeemOptions
      : [
          { days: 7, label: '快速额度释放', feeRate: 0.1 },
          { days: 30, label: '标准额度释放', feeRate: 0 }
        ]

    return source
      .map(item => ({
        days: Number(item.days || item.releaseDays || 0),
        label: item.label || item.name || '',
        feeRate: this.normalizeRate(
          item.feeRate !== undefined ? item.feeRate : item.feeRateBps
        )
      }))
      .map(item => ({
        ...item,
        feeRate: item.feeRate > 1 ? item.feeRate / 10000 : item.feeRate
      }))
      .filter(item => item.days > 0)
  },
  selectedOption() {
    return this.normalizedOptions.find(item => item.days === Number(this.selectedDays)) ||
      this.normalizedOptions[0] ||
      { days: 7, label: '', feeRate: 0 }
  },
  redeemUnitRate() {
    const raw = this.order && this.order.redeemUnitRate
    if (raw === undefined || raw === null || raw === '') return 0.2

    const value = Number(raw)
    return value > 1 ? value / 100 : value
  },
  redeemUnitPercent() {
    return Number((this.redeemUnitRate * 100).toFixed(2))
  },
  redeemTotalTimes() {
    const value = Number((this.order && this.order.redeemTotalTimes) || 5)
    return value > 0 ? value : 5
  },
  redeemAppliedTimes() {
    return Math.max(Number((this.order && this.order.redeemAppliedTimes) || 0), 0)
  },
  arbitragePrincipalUsdt() {
    const item = this.order || {}

    if (item.arbitragePrincipalUsdt !== undefined && item.arbitragePrincipalUsdt !== null) {
      return Number(item.arbitragePrincipalUsdt || 0)
    }

    return Math.max(
      Number(item.depositAmountUsdt || 0) - Number(item.modelFeeUsdt || 0),
      0
    )
  },
  redeemPrincipalUsdt() {
    const standardAmount = this.arbitragePrincipalUsdt * this.redeemUnitRate
    const earning = Number((this.order && this.order.earningPrincipalUsdt) || 0)
    return Math.max(Math.min(standardAmount, earning), 0)
  },
  redeemFeeRate() {
    return this.normalizeRate(this.selectedOption.feeRate)
  },
  redeemFeeUsdt() {
    return this.redeemPrincipalUsdt * this.redeemFeeRate
  },
  netRedeemUsdt() {
    return Math.max(this.redeemPrincipalUsdt - this.redeemFeeUsdt, 0)
  },
  dailyNetReleaseUsdt() {
    const days = Number(this.selectedDays || 0)
    if (days <= 0) return 0
    return this.netRedeemUsdt / days
  },
  remainingEarningPrincipal() {
    return Math.max(
      Number((this.order && this.order.earningPrincipalUsdt) || 0) -
        this.redeemPrincipalUsdt,
      0
    )
  },
  estimatedBtc() {
    const price = Number(this.btcPriceUsdt || 0)
    if (price <= 0) return 0
    return this.netRedeemUsdt / price
  },
  nextRedeemStep() {
    return this.redeemAppliedTimes + 1
  },
  trialProfitUsdt() {
    return Number((this.order && this.order.trialProfitUsdt) || 0)
  },
  trialClaimUsdt() {
    const item = this.order || {}

    if (item.expectedTotalUsdt !== undefined && item.expectedTotalUsdt !== null) {
      return Number(item.expectedTotalUsdt || 0)
    }

    return Number(item.depositAmountUsdt || 0) + this.trialProfitUsdt
  },
  trialAlreadyClaimed() {
    const item = this.order || {}
    return Number(item.status) === 3 ||
      Number(item.claimStatus) === 2 ||
      Number(item.trialClaimStatus) === 2
  },
  trialClaimProcessing() {
    const item = this.order || {}
    return Number(item.claimStatus) === 1 ||
      Number(item.trialClaimStatus) === 1
  },
  trialMatured() {
    const item = this.order || {}
    const matured = Boolean(item.canWithdraw) || Number(item.status) === 2

    return this.isTrial &&
      matured &&
      !this.trialAlreadyClaimed &&
      !this.trialClaimProcessing
  },
  trialMaturityText() {
    if (this.trialAlreadyClaimed) {
      return '该体验订单已经完成到期统一结算。'
    }

    if (this.trialClaimProcessing) {
      return '该体验策略的结算申请正在处理中，请勿重复提交。'
    }

    if (this.trialMatured) {
      return '体验策略周期已经完成，当前可提交配置额度与预设结算结果。'
    }

    const remaining = Number((this.order && this.order.remainingDays) || 0)
    return remaining > 0
      ? '体验订单周期尚未完成，预计剩余 ' + remaining + ' 天开放到期结算。'
      : '体验策略尚未达到系统确认的到期结算状态。'
  },
  standardCanRedeem() {
    const item = this.order || {}

    return !this.isTrial &&
      Number(item.status) === 1 &&
      Number(item.earningPrincipalUsdt || 0) > 0 &&
      Number(item.redeemStatus || 0) !== 2 &&
      this.redeemAppliedTimes < this.redeemTotalTimes &&
      this.redeemPrincipalUsdt > 0 &&
      Number(this.selectedDays || 0) > 0
  },
  canSubmit() {
    return this.isTrial ? this.trialMatured : this.standardCanRedeem
  },
  actionStatusText() {
    if (this.isTrial) {
      if (this.trialAlreadyClaimed) return '已完成结算'
      if (this.trialClaimProcessing) return '结算处理中'
      return this.trialMatured ? '可提交到期结算' : '周期尚未完成'
    }

    return this.standardCanRedeem ? '可申请额度释放' : '暂不可释放'
  },
  submitButtonText() {
    if (this.submitting) return '正在提交...'

    if (this.isTrial) {
      if (this.trialAlreadyClaimed) return '体验订单已完成结算'
      if (this.trialClaimProcessing) return '体验订单结算处理中'
      return this.trialMatured ? '确认到期统一结算' : '体验订单周期尚未完成'
    }

    return this.standardCanRedeem
      ? '确认提交额度释放'
      : '当前策略暂不可释放'
  }
},
watch: {
  open: {
    immediate: true,
    handler(value) {
      if (value) {
        const first = this.normalizedOptions[0]
        this.selectedDays = first ? first.days : 7
        this.confirmed = false
        this.internalLoading = false
      }

      if (typeof document !== 'undefined') {
        document.body.style.overflow = value ? 'hidden' : ''
      }
    }
  },
  normalizedOptions: {
    deep: true,
    handler(options) {
      if (!options.some(item => item.days === Number(this.selectedDays))) {
        this.selectedDays = options[0] ? options[0].days : 7
      }
    }
  }
},
beforeDestroy() {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
  }
},
methods: {
  close() {
    if (this.submitting) return
    this.$emit('close')
  },
  async submit() {
    if (!this.canSubmit || !this.confirmed || this.submitting || !this.order) {
      return
    }

    const orderNo = String(this.order.orderNo || '').trim()
    if (!orderNo) {
      return this.notify('策略编号不存在，无法提交操作。', 'error')
    }

    this.internalLoading = true

    try {
      let response
      let action

      if (this.isTrial) {
        action = 'TRIAL_MATURITY_SETTLEMENT'
        response = await claimBchTrialOrder({
          orderNo
        }, {
          silent: true
        })

        this.notify({
          title: '体验策略结算申请成功',
          desc: '该订单的体验配置额度与固定收益已经进入提取处理流程。',
          duration: 3200
        }, 'success')
      } else {
        action = 'STANDARD_ALLOCATION_RELEASE'
        response = await applyBchPrincipalRedeem({
          orderNo,
          releaseDays: Number(this.selectedDays)
        }, {
          silent: true
        })

        this.notify({
          title: '额度释放申请成功',
          desc: '本次本金已经停止计息，并进入所选周期的额度释放流程。',
          duration: 3200
        }, 'success')
      }

      const result = {
        action,
        orderNo,
        orderType: this.isTrial ? 2 : 1,
        releaseDays: this.isTrial ? null : Number(this.selectedDays),
        response
      }

      this.$emit('success', result)
      this.$emit('refresh', result)

      if (typeof window !== 'undefined' && typeof window.CustomEvent === 'function') {
        window.dispatchEvent(new CustomEvent('bch:orders:changed', {
          detail: result
        }))
      }

      this.confirmed = false
      this.$emit('close')
    } catch (error) {
      console.error('BCH 策略释放/结算失败：', error)
      this.notify(this.extractErrorMessage(
        error,
        this.isTrial
          ? '体验策略结算失败，请稍后重试。'
          : '额度释放申请失败，请稍后重试。'
      ), 'error')
    } finally {
      this.internalLoading = false
    }
  },
  extractErrorMessage(error, fallback) {
    if (!error) return fallback

    const responseData = error.response && error.response.data
    if (responseData) {
      return responseData.msg ||
        responseData.message ||
        responseData.error ||
        fallback
    }

    return error.msg ||
      error.message ||
      error.reason ||
      fallback
  },
  notify(message, type = 'info') {
    if (this.$toast) {
      if (typeof message === 'string') {
        if (typeof this.$toast[type] === 'function') {
          return this.$toast[type](message)
        }
        if (typeof this.$toast.show === 'function') {
          return this.$toast.show(message)
        }
        if (typeof this.$toast === 'function') {
          return this.$toast(message)
        }
      } else {
        if (typeof this.$toast.show === 'function') {
          return this.$toast.show(message)
        }
        if (typeof this.$toast === 'function') {
          return this.$toast(message)
        }
      }
    }

    const text = typeof message === 'string'
      ? message
      : (message && (message.desc || message.title)) || '系统提示'

    if (this.$message) {
      if (typeof this.$message[type] === 'function') {
        return this.$message[type](text)
      }
      if (typeof this.$message === 'function') {
        return this.$message(text)
      }
    }

    alert(text)
  },
  normalizeRate(value) {
    const numeric = Number(value || 0)
    if (!Number.isFinite(numeric) || numeric <= 0) return 0

    if (numeric > 100) return numeric / 10000
    if (numeric > 1) return numeric / 100
    return numeric
  },
  money(value) {
    return Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    })
  },
  btc(value) {
    return Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 8,
      maximumFractionDigits: 8
    })
  },
  percent(value) {
    const result = this.normalizeRate(value) * 100
    return result.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }) + '%'
  }
}
}
</script>

<style lang="less" scoped>
.redeem-mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: rgba(2, 6, 14, .78);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.redeem-panel {
  width: 94vw;
  max-width: 700px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  color: #fff;
  background:
    radial-gradient(circle at 100% 0, rgba(212, 175, 55, .12), transparent 28%),
    linear-gradient(180deg, #09111f 0%, #070c16 52%, #050810 100%);
  border-left: 1px solid rgba(255, 255, 255, .09);
  box-shadow: -28px 0 80px rgba(0, 0, 0, .5);
}

.redeem-header {
  flex-shrink: 0;
  min-height: 78px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: rgba(7, 12, 22, .9);
  border-bottom: 1px solid rgba(255, 255, 255, .07);
}

.header-copy span {
  display: block;
  color: #7e92b5;
  font-size: 9px;
  font-weight: 950;
  letter-spacing: 1.1px;
}

.header-copy h2 {
  margin: 5px 0 0;
  font-size: 20px;
}

.close-btn {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border-radius: 13px;
  color: #a8bad8;
  background: rgba(255, 255, 255, .055);
  border: 1px solid rgba(255, 255, 255, .08);
  font-size: 24px;
  cursor: pointer;
}

.redeem-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 26px;
  -webkit-overflow-scrolling: touch;
}

.order-summary {
  position: relative;
  overflow: hidden;
  padding: 17px 15px;
  border-radius: 24px;
  background:
    radial-gradient(circle at 100% 0, rgba(212, 175, 55, .16), transparent 34%),
    linear-gradient(145deg, rgba(20, 31, 52, .96), rgba(8, 14, 25, .98));
  border: 1px solid rgba(212, 175, 55, .2);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .05), 0 18px 42px rgba(0, 0, 0, .23);
}

.order-summary.trial {
  background:
    radial-gradient(circle at 100% 0, rgba(59, 130, 246, .2), transparent 35%),
    linear-gradient(145deg, rgba(14, 32, 65, .96), rgba(7, 14, 28, .98));
  border-color: rgba(59, 130, 246, .24);
}

.summary-glow {
  position: absolute;
  right: -60px;
  top: -60px;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: rgba(212, 175, 55, .11);
  filter: blur(30px);
}

.order-summary.trial .summary-glow {
  background: rgba(59, 130, 246, .14);
}

.summary-top {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.type-badge {
  min-height: 23px;
  padding: 0 9px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  color: #ffe08a;
  background: rgba(212, 175, 55, .11);
  border: 1px solid rgba(212, 175, 55, .19);
  font-size: 8px;
  font-weight: 950;
}

.type-badge.trial {
  color: #93c5fd;
  background: rgba(59, 130, 246, .12);
  border-color: rgba(59, 130, 246, .2);
}

.summary-top h3 {
  margin: 9px 0 0;
  font-size: 15px;
}

.summary-top p {
  margin: 5px 0 0;
  color: #65799b;
  font-size: 9px;
}

.status-badge {
  flex-shrink: 0;
  min-height: 27px;
  padding: 0 9px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 9px;
  font-weight: 900;
}

.status-badge i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 8px currentColor;
}

.status-badge.ready {
  color: #4ade80;
  background: rgba(34, 197, 94, .1);
}

.status-badge.disabled {
  color: #94a3b8;
  background: rgba(148, 163, 184, .1);
}

.summary-assets {
  position: relative;
  z-index: 1;
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.summary-assets > div {
  min-width: 0;
  padding: 13px 11px;
  border-radius: 17px;
  background: rgba(255, 255, 255, .045);
  border: 1px solid rgba(255, 255, 255, .065);
}

.summary-assets span,
.summary-assets strong,
.summary-assets em {
  display: block;
}

.summary-assets span {
  color: #8499bb;
  font-size: 10px;
}

.summary-assets strong {
  overflow: hidden;
  margin-top: 7px;
  font-size: 20px;
  text-overflow: ellipsis;
}

.summary-assets strong.positive {
  color: #4ade80;
}

.summary-assets em {
  margin-top: 4px;
  color: #ffe08a;
  font-size: 9px;
  font-style: normal;
}

.order-summary.trial .summary-assets em {
  color: #60a5fa;
}

.redeem-section {
  margin-top: 13px;
  padding: 15px 13px;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(17, 27, 46, .83), rgba(8, 14, 25, .91));
  border: 1px solid rgba(148, 163, 184, .12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .035);
}

.section-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.section-title span {
  display: block;
  color: #65799c;
  font-size: 8px;
  font-weight: 950;
  letter-spacing: .85px;
}

.section-title h3 {
  margin: 5px 0 0;
  font-size: 15px;
}

.section-title > b {
  flex-shrink: 0;
  padding: 5px 8px;
  border-radius: 999px;
  color: #ffe08a;
  background: rgba(212, 175, 55, .09);
  font-size: 8px;
  letter-spacing: .3px;
}

.trial-section .section-title > b {
  color: #93c5fd;
  background: rgba(59, 130, 246, .1);
}

.period-grid {
  margin-top: 13px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.period-card {
  position: relative;
  min-width: 0;
  min-height: 91px;
  padding: 13px 10px;
  border-radius: 17px;
  color: #fff;
  text-align: left;
  background: rgba(255, 255, 255, .04);
  border: 1px solid rgba(255, 255, 255, .07);
  cursor: pointer;
}

.period-card.active {
  background: rgba(212, 175, 55, .09);
  border-color: rgba(212, 175, 55, .35);
  box-shadow: inset 0 0 0 1px rgba(212, 175, 55, .05);
}

.period-check {
  position: absolute;
  right: 10px;
  top: 10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, .17);
}

.period-card.active .period-check::after {
  content: '';
  position: absolute;
  inset: 4px;
  border-radius: 50%;
  background: #ffe08a;
  box-shadow: 0 0 9px rgba(255, 224, 138, .7);
}

.period-card strong,
.period-card small,
.period-card em {
  display: block;
}

.period-card strong {
  padding-right: 20px;
  font-size: 18px;
}

.period-card small {
  margin-top: 5px;
  color: #7589aa;
  font-size: 9px;
}

.period-card em {
  margin-top: 11px;
  color: #ffe08a;
  font-size: 9px;
  font-style: normal;
  font-weight: 900;
}

.release-notice,
.maturity-note {
  margin-top: 12px;
  padding: 11px;
  border-radius: 15px;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  color: #d4c58f;
  background: rgba(212, 175, 55, .07);
  border: 1px solid rgba(212, 175, 55, .15);
}

.release-notice > span,
.maturity-note > span {
  flex: 0 0 21px;
  width: 21px;
  height: 21px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #080b12;
  background: #ffe08a;
  font-size: 10px;
  font-weight: 950;
}

.release-notice p,
.maturity-note p {
  margin: 0;
  font-size: 10px;
  line-height: 1.7;
}

.calc-list {
  margin-top: 13px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.calc-list > div {
  min-width: 0;
  padding: 11px 10px;
  border-radius: 15px;
  background: rgba(255, 255, 255, .038);
  border: 1px solid rgba(255, 255, 255, .06);
}

.calc-list span,
.calc-list strong {
  display: block;
}

.calc-list span {
  color: #778bad;
  font-size: 9px;
}

.calc-list strong {
  overflow: hidden;
  margin-top: 7px;
  color: #eef5ff;
  font-size: 12px;
  text-overflow: ellipsis;
}

.calc-list strong.negative {
  color: #fca5a5;
}

.calc-list .btc-result {
  background: rgba(212, 175, 55, .08);
  border-color: rgba(212, 175, 55, .18);
}

.calc-list .btc-result strong {
  color: #ffe08a;
}

.price-tip {
  margin: 10px 0 0;
  color: #63779a;
  font-size: 9px;
  line-height: 1.65;
}

.principal-change {
  margin-top: 13px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 26px minmax(0, 1fr);
  align-items: center;
  gap: 6px;
}

.principal-change > div:not(.arrow) {
  min-width: 0;
  padding: 12px 10px;
  border-radius: 16px;
  background: rgba(255, 255, 255, .04);
  border: 1px solid rgba(255, 255, 255, .06);
}

.principal-change .after {
  background: rgba(59, 130, 246, .07);
  border-color: rgba(59, 130, 246, .15);
}

.principal-change span,
.principal-change strong,
.principal-change em {
  display: block;
}

.principal-change span {
  color: #778bad;
  font-size: 9px;
}

.principal-change strong {
  margin-top: 6px;
  font-size: 16px;
}

.principal-change em {
  margin-top: 3px;
  color: #60a5fa;
  font-size: 8px;
  font-style: normal;
}

.principal-change .arrow {
  color: #7186a8;
  text-align: center;
  font-size: 17px;
}

.trial-timeline {
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
}

.timeline-item {
  flex: 0 0 76px;
  width: 76px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.timeline-item > i {
  width: 27px;
  height: 27px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7186a8;
  background: rgba(255, 255, 255, .05);
  border: 1px solid rgba(255, 255, 255, .08);
  font-size: 10px;
  font-style: normal;
  font-weight: 950;
}

.timeline-item.done > i {
  color: #fff;
  background: #2563eb;
  border-color: #60a5fa;
  box-shadow: 0 0 16px rgba(59, 130, 246, .28);
}

.timeline-item span,
.timeline-item b,
.timeline-item small {
  display: block;
}

.timeline-item span {
  margin-top: 8px;
}

.timeline-item b {
  font-size: 9px;
}

.timeline-item small {
  margin-top: 3px;
  color: #63779a;
  font-size: 7px;
}

.timeline-line {
  flex: 1;
  height: 1px;
  margin-top: 13px;
  background: linear-gradient(90deg, rgba(59, 130, 246, .75), rgba(255, 255, 255, .08));
}

.trial-settlement-card {
  margin-top: 16px;
  padding: 12px 10px;
  border-radius: 18px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 18px minmax(0, 1fr) 18px minmax(0, 1.2fr);
  align-items: center;
  gap: 4px;
  background: rgba(59, 130, 246, .07);
  border: 1px solid rgba(59, 130, 246, .15);
}

.trial-settlement-card > div:not(.plus):not(.equal) {
  min-width: 0;
}

.trial-settlement-card span,
.trial-settlement-card strong,
.trial-settlement-card em {
  display: block;
}

.trial-settlement-card span {
  color: #8299be;
  font-size: 8px;
}

.trial-settlement-card strong {
  overflow: hidden;
  margin-top: 5px;
  font-size: 13px;
  text-overflow: ellipsis;
}

.trial-settlement-card em {
  margin-top: 3px;
  color: #60a5fa;
  font-size: 7px;
  font-style: normal;
}

.trial-settlement-card .plus,
.trial-settlement-card .equal {
  color: #7186a8;
  text-align: center;
  font-weight: 950;
}

.trial-settlement-card .total strong {
  color: #93c5fd;
}

.maturity-note.ready {
  color: #bdeccd;
  background: rgba(34, 197, 94, .07);
  border-color: rgba(34, 197, 94, .15);
}

.maturity-note.ready > span {
  color: #fff;
  background: #22c55e;
}

.maturity-note.waiting {
  color: #b6c4d9;
  background: rgba(148, 163, 184, .07);
  border-color: rgba(148, 163, 184, .14);
}

.maturity-note.waiting > span {
  color: #fff;
  background: #64748b;
}

.rule-list {
  margin-top: 12px;
  display: grid;
  gap: 8px;
}

.rule-list > div {
  padding: 10px;
  border-radius: 14px;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  background: rgba(255, 255, 255, .035);
  border: 1px solid rgba(255, 255, 255, .055);
}

.rule-list i {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #93c5fd;
  background: rgba(59, 130, 246, .1);
  font-size: 8px;
  font-style: normal;
  font-weight: 950;
}

.rule-list p,
.rule-list b,
.rule-list span {
  display: block;
}

.rule-list p {
  margin: 0;
}

.rule-list b {
  font-size: 10px;
}

.rule-list span {
  margin-top: 3px;
  color: #6f83a5;
  font-size: 9px;
  line-height: 1.5;
}

.confirm-row {
  margin-top: 13px;
  padding: 12px;
  border-radius: 16px;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  cursor: pointer;
  background: rgba(255, 255, 255, .035);
  border: 1px solid rgba(255, 255, 255, .06);
}

.confirm-row input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.fake-check {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  background: rgba(255, 255, 255, .05);
  border: 1px solid rgba(255, 255, 255, .13);
  font-size: 10px;
  font-weight: 950;
}

.confirm-row input:checked + .fake-check {
  color: #080b12;
  background: #ffe08a;
  border-color: #ffe08a;
}

.confirm-row p {
  margin: 0;
  color: #8094b5;
  font-size: 9px;
  line-height: 1.6;
}

.redeem-footer {
  flex-shrink: 0;
  padding: 12px 14px calc(12px + env(safe-area-inset-bottom));
  display: grid;
  grid-template-columns: .8fr 1.4fr;
  gap: 9px;
  background: rgba(6, 10, 18, .96);
  border-top: 1px solid rgba(255, 255, 255, .07);
}

.redeem-footer button {
  min-width: 0;
  min-height: 48px;
  border-radius: 15px;
  font-size: 11px;
  font-weight: 950;
  cursor: pointer;
}

.cancel-btn {
  color: #a4b4ce;
  background: rgba(255, 255, 255, .055);
  border: 1px solid rgba(255, 255, 255, .08);
}

.confirm-btn {
  border: none;
}

.confirm-btn.standard {
  color: #080b12;
  background: linear-gradient(135deg, #d4af37, #ffe08a);
  box-shadow: 0 12px 28px rgba(212, 175, 55, .2);
}

.confirm-btn.trial {
  color: #fff;
  background: linear-gradient(135deg, #1d4ed8, #60a5fa);
  box-shadow: 0 12px 28px rgba(37, 99, 235, .22);
}

.redeem-footer button:disabled {
  opacity: .42;
  cursor: not-allowed;
  box-shadow: none;
}

.loading-dot {
  width: 10px;
  height: 10px;
  margin-right: 6px;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid currentColor;
  border-right-color: transparent;
  vertical-align: -1px;
  animation: loadingRotate .75s linear infinite;
}

@keyframes loadingRotate {
  to { transform: rotate(360deg); }
}

.redeem-fade-enter-active,
.redeem-fade-leave-active {
  transition: opacity .25s ease;
}

.redeem-fade-enter,
.redeem-fade-leave-to {
  opacity: 0;
}

.redeem-slide-enter-active,
.redeem-slide-leave-active {
  transition: transform .28s ease;
}

.redeem-slide-enter,
.redeem-slide-leave-to {
  transform: translateX(100%);
}

@media (max-width: 480px) {
  .redeem-panel {
    width: 100vw;
    max-width: none;
    border-left: none;
  }

  .period-grid,
  .calc-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .trial-timeline {
    justify-content: space-between;
  }

  .timeline-item {
    flex-basis: 70px;
    width: 70px;
  }
}

@media (min-width: 768px) {
  .redeem-body {
    padding: 18px 18px 30px;
  }

  .redeem-header {
    padding: 18px 22px;
  }

  .redeem-section,
  .order-summary {
    padding: 18px;
  }

  .period-card {
    min-height: 102px;
    padding: 16px 14px;
  }

  .calc-list {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}


/* =========================================================
 * PROFESSIONAL RELEASE & SETTLEMENT DRAWER
 * 冷紫 + 青色电光，统一策略管理终端视觉
 * ========================================================= */

.redeem-panel {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-left: 1px solid rgba(103, 232, 249, .14);
  background:
    radial-gradient(circle at 88% 0%, rgba(124, 58, 237, .2), transparent 28%),
    radial-gradient(circle at 0% 34%, rgba(6, 182, 212, .16), transparent 32%),
    linear-gradient(180deg, #050814 0%, #080d1c 53%, #050910 100%);
}

/* 背景网格 */
.redeem-grid-bg {
  position: absolute;
  z-index: -4;
  inset: 0;
  opacity: .47;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(103, 232, 249, .028) 1px, transparent 1px),
    linear-gradient(90deg, rgba(167, 139, 250, .026) 1px, transparent 1px);
  background-size: 28px 28px;
  animation: redeemGridMove 20s linear infinite;
}

/* 横向扫描光 */
.redeem-scan-beam {
  position: absolute;
  z-index: -2;
  top: -160px;
  bottom: -160px;
  left: -160px;
  width: 135px;
  transform: skewX(-21deg);
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(103, 232, 249, .045),
    rgba(255, 255, 255, .08),
    transparent
  );
  animation: redeemBeamSweep 9s ease-in-out infinite;
}

/* 极光 */
.redeem-aurora {
  position: absolute;
  z-index: -3;
  width: 270px;
  height: 270px;
  border-radius: 50%;
  filter: blur(76px);
  pointer-events: none;
}

.redeem-aurora-cyan {
  left: -160px;
  top: 26%;
  background: rgba(6, 182, 212, .2);
  animation: redeemAuroraFloat 9s ease-in-out infinite;
}

.redeem-aurora-violet {
  right: -160px;
  bottom: 8%;
  background: rgba(124, 58, 237, .22);
  animation: redeemAuroraFloat 11s ease-in-out infinite reverse;
}

/* 粒子 */
.redeem-particles {
  position: absolute;
  z-index: -1;
  inset: 0;
  pointer-events: none;
}

.redeem-particles i {
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #67e8f9;
  box-shadow: 0 0 10px rgba(103, 232, 249, .82);
  animation: redeemParticleFloat 6s ease-in-out infinite;
}

.redeem-particles i:nth-child(1) {
  top: 15%;
  left: 8%;
}

.redeem-particles i:nth-child(2) {
  top: 36%;
  right: 9%;
  background: #c4b5fd;
  animation-delay: 1.4s;
}

.redeem-particles i:nth-child(3) {
  top: 65%;
  left: 14%;
  background: #ffe08a;
  animation-delay: 2.8s;
}

.redeem-particles i:nth-child(4) {
  right: 12%;
  bottom: 9%;
  animation-delay: 4.1s;
}

/* 顶部终端化 */
.redeem-header {
  position: relative;
  z-index: 10;
  overflow: hidden;
  background:
    radial-gradient(circle at 90% 0%, rgba(124, 58, 237, .14), transparent 38%),
    linear-gradient(180deg, rgba(7, 12, 25, .97), rgba(5, 9, 18, .92));
  border-bottom: 1px solid rgba(103, 232, 249, .11);
  box-shadow:
    inset 0 -1px 0 rgba(167, 139, 250, .05),
    0 12px 34px rgba(0, 0, 0, .22);
}

.redeem-header::after {
  position: absolute;
  left: 11%;
  right: 11%;
  bottom: 0;
  height: 1px;
  content: "";
  opacity: .6;
  background: linear-gradient(
    90deg,
    transparent,
    #22d3ee,
    #8b5cf6,
    transparent
  );
  background-size: 180% 100%;
  animation: redeemHeaderLine 5s linear infinite;
}

.header-copy span {
  color: #67e8f9;
  text-shadow: 0 0 12px rgba(103, 232, 249, .2);
}

.close-btn {
  position: relative;
  overflow: hidden;
  border-color: rgba(103, 232, 249, .14);
  background:
    linear-gradient(145deg, rgba(24, 34, 58, .88), rgba(7, 13, 25, .94));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .06),
    0 8px 22px rgba(0, 0, 0, .24);
  transition: transform .2s ease, border-color .2s ease, color .2s ease;
}

.close-btn:hover {
  color: #a5f3fc;
  border-color: rgba(103, 232, 249, .32);
  transform: translateY(-1px);
}

/* 顶部策略摘要 */
.order-summary {
  isolation: isolate;
  border-color: rgba(103, 232, 249, .18);
  background:
    radial-gradient(circle at 88% 4%, rgba(124, 58, 237, .28), transparent 30%),
    radial-gradient(circle at 0% 100%, rgba(6, 182, 212, .2), transparent 35%),
    linear-gradient(145deg, rgba(13, 22, 43, .98), rgba(5, 11, 23, .99));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .075),
    0 24px 60px rgba(0, 0, 0, .4),
    0 0 28px rgba(34, 211, 238, .035);
}

.order-summary::before {
  position: absolute;
  z-index: 1;
  top: 0;
  left: -85px;
  width: 85px;
  height: 100%;
  content: "";
  transform: skewX(-22deg);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, .08),
    transparent
  );
  animation: redeemSummaryShine 7s ease-in-out infinite;
  pointer-events: none;
}

.order-summary::after {
  position: absolute;
  z-index: 1;
  left: 14%;
  right: 14%;
  top: 0;
  height: 1px;
  content: "";
  opacity: .62;
  background: linear-gradient(
    90deg,
    transparent,
    #67e8f9,
    #a78bfa,
    transparent
  );
}

.order-summary.trial {
  border-color: rgba(96, 165, 250, .24);
  background:
    radial-gradient(circle at 88% 4%, rgba(37, 99, 235, .3), transparent 30%),
    radial-gradient(circle at 0% 100%, rgba(124, 58, 237, .2), transparent 35%),
    linear-gradient(145deg, rgba(13, 23, 48, .98), rgba(5, 11, 24, .99));
}

.summary-glow {
  animation: redeemGlowPulse 6s ease-in-out infinite;
}

.status-badge.ready i {
  animation: redeemStatusPulse 1.7s ease-in-out infinite;
}

.summary-top h3 {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  letter-spacing: -.3px;
}

.summary-assets > div {
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, .07), rgba(255, 255, 255, .025));
  border-color: rgba(103, 232, 249, .09);
  transition: transform .22s ease, border-color .22s ease, background .22s ease;
}

.summary-assets > div::after {
  position: absolute;
  left: 14%;
  right: 14%;
  top: 0;
  height: 1px;
  content: "";
  background: linear-gradient(
    90deg,
    transparent,
    rgba(103, 232, 249, .42),
    transparent
  );
}

.summary-assets > div:hover {
  transform: translateY(-2px);
  border-color: rgba(103, 232, 249, .2);
  background:
    linear-gradient(145deg, rgba(34, 211, 238, .075), rgba(255, 255, 255, .026));
}

/* 内容区卡片 */
.redeem-section {
  position: relative;
  overflow: hidden;
  border-color: rgba(129, 140, 248, .12);
  background:
    radial-gradient(circle at 100% 0%, rgba(99, 102, 241, .065), transparent 30%),
    linear-gradient(180deg, rgba(14, 23, 43, .9), rgba(6, 12, 24, .96));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .05),
    0 18px 44px rgba(0, 0, 0, .24);
  transition: transform .22s ease, border-color .22s ease;
}

.redeem-section::before {
  position: absolute;
  top: 0;
  left: 12%;
  right: 12%;
  height: 1px;
  content: "";
  background: linear-gradient(
    90deg,
    transparent,
    rgba(103, 232, 249, .28),
    rgba(167, 139, 250, .2),
    transparent
  );
}

.redeem-section:hover {
  transform: translateY(-2px);
  border-color: rgba(103, 232, 249, .18);
}

.section-title > b {
  color: #a5f3fc;
  background: rgba(6, 182, 212, .08);
  border: 1px solid rgba(34, 211, 238, .11);
}

/* 释放周期选项 */
.period-card {
  overflow: hidden;
  transition: transform .22s ease, border-color .22s ease, background .22s ease;
}

.period-card::before {
  position: absolute;
  top: -18px;
  bottom: -18px;
  left: -45px;
  width: 28px;
  content: "";
  transform: skewX(-22deg);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, .11),
    transparent
  );
  opacity: 0;
  pointer-events: none;
}

.period-card:hover {
  transform: translateY(-2px);
  border-color: rgba(103, 232, 249, .2);
}

.period-card.active {
  background:
    linear-gradient(145deg, rgba(6, 182, 212, .11), rgba(124, 58, 237, .075));
  border-color: rgba(103, 232, 249, .28);
  box-shadow:
    inset 0 0 0 1px rgba(103, 232, 249, .035),
    0 12px 26px rgba(0, 0, 0, .16);
}

.period-card.active::before {
  opacity: 1;
  animation: redeemPeriodShine 4.6s ease-in-out infinite;
}

.period-card.active .period-check::after {
  background: #67e8f9;
  box-shadow: 0 0 10px rgba(103, 232, 249, .72);
}

.period-card em {
  color: #a5f3fc;
}

/* 测算卡片 */
.calc-list > div,
.principal-change > div:not(.arrow),
.rule-list > div {
  transition: transform .2s ease, background .2s ease, border-color .2s ease;
}

.calc-list > div:hover,
.principal-change > div:not(.arrow):hover,
.rule-list > div:hover {
  transform: translateY(-1px);
  background: rgba(103, 232, 249, .045);
  border-color: rgba(103, 232, 249, .11);
}

.calc-list .btc-result {
  background:
    linear-gradient(145deg, rgba(6, 182, 212, .11), rgba(124, 58, 237, .075));
  border-color: rgba(103, 232, 249, .18);
}

.calc-list .btc-result strong {
  color: #67e8f9;
}

.principal-change .after {
  background:
    linear-gradient(145deg, rgba(37, 99, 235, .1), rgba(124, 58, 237, .06));
  border-color: rgba(96, 165, 250, .16);
}

/* 时间线流动效果 */
.timeline-line {
  position: relative;
  overflow: hidden;
}

.timeline-line::after {
  position: absolute;
  inset: 0;
  content: "";
  background: linear-gradient(
    90deg,
    transparent,
    #67e8f9,
    transparent
  );
  transform: translateX(-100%);
  animation: redeemTimelineFlow 2.4s linear infinite;
}

.timeline-item.done > i {
  background: linear-gradient(135deg, #2563eb, #8b5cf6);
  border-color: #67e8f9;
  box-shadow:
    0 0 18px rgba(59, 130, 246, .3),
    0 0 24px rgba(139, 92, 246, .14);
}

/* 体验结算卡片 */
.trial-settlement-card {
  background:
    linear-gradient(145deg, rgba(37, 99, 235, .09), rgba(124, 58, 237, .07));
  border-color: rgba(96, 165, 250, .16);
}

.trial-settlement-card .total strong {
  color: #67e8f9;
}

.rule-list i {
  color: #a5f3fc;
  background: rgba(6, 182, 212, .09);
  border: 1px solid rgba(34, 211, 238, .11);
}

/* 确认区域 */
.confirm-row {
  transition: border-color .2s ease, background .2s ease, transform .2s ease;
}

.confirm-row:hover {
  transform: translateY(-1px);
  border-color: rgba(103, 232, 249, .14);
  background: rgba(6, 182, 212, .035);
}

.confirm-row input:checked + .fake-check {
  color: #041016;
  background: linear-gradient(135deg, #67e8f9, #a78bfa);
  border-color: transparent;
  box-shadow: 0 0 14px rgba(103, 232, 249, .18);
}

/* 底部操作栏 */
.redeem-footer {
  position: relative;
  z-index: 12;
  border-top: 1px solid rgba(103, 232, 249, .08);
  background:
    linear-gradient(
      180deg,
      rgba(5, 9, 18, .4),
      rgba(5, 9, 18, .98) 28%
    );
  box-shadow: 0 -18px 42px rgba(0, 0, 0, .28);
}

.redeem-footer button {
  position: relative;
  overflow: hidden;
  transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
}

.redeem-footer button:not(:disabled):hover {
  transform: translateY(-2px);
}

.cancel-btn {
  border-color: rgba(103, 232, 249, .12);
  background:
    linear-gradient(145deg, rgba(20, 31, 52, .82), rgba(8, 14, 27, .92));
}

.confirm-btn.standard,
.confirm-btn.trial {
  background:
    linear-gradient(135deg, #67e8f9 0%, #60a5fa 48%, #a78bfa 100%);
  box-shadow:
    0 14px 32px rgba(59, 130, 246, .24),
    0 0 24px rgba(103, 232, 249, .08);
}

.confirm-btn::before {
  position: absolute;
  top: -20px;
  bottom: -20px;
  left: -46px;
  width: 28px;
  content: "";
  transform: skewX(-22deg);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, .45),
    transparent
  );
  animation: redeemButtonShine 4.8s ease-in-out infinite;
}

/* 自定义滚动条 */
.redeem-body {
  scrollbar-width: thin;
  scrollbar-color: rgba(103, 232, 249, .28) rgba(255, 255, 255, .03);
}

.redeem-body::-webkit-scrollbar {
  width: 5px;
}

.redeem-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, .025);
}

.redeem-body::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: linear-gradient(
    180deg,
    rgba(34, 211, 238, .52),
    rgba(139, 92, 246, .5)
  );
}

/* 动画 */
@keyframes redeemGridMove {
  from { background-position: 0 0; }
  to { background-position: 56px 56px; }
}

@keyframes redeemBeamSweep {
  0%, 18% {
    left: -170px;
    opacity: 0;
  }
  30% { opacity: .7; }
  68% {
    left: calc(100% + 160px);
    opacity: .25;
  }
  100% {
    left: calc(100% + 160px);
    opacity: 0;
  }
}

@keyframes redeemAuroraFloat {
  0%, 100% {
    opacity: .46;
    transform: translate3d(0, 0, 0) scale(.9);
  }
  50% {
    opacity: .92;
    transform: translate3d(18px, -16px, 0) scale(1.08);
  }
}

@keyframes redeemParticleFloat {
  0%, 100% {
    opacity: .2;
    transform: translateY(9px) scale(.75);
  }
  50% {
    opacity: 1;
    transform: translateY(-12px) scale(1.18);
  }
}

@keyframes redeemHeaderLine {
  from { background-position: 0% 50%; }
  to { background-position: 180% 50%; }
}

@keyframes redeemSummaryShine {
  0%, 18% {
    left: -85px;
    opacity: 0;
  }
  30% { opacity: 1; }
  52% {
    left: calc(100% + 85px);
    opacity: 0;
  }
  100% {
    left: calc(100% + 85px);
    opacity: 0;
  }
}

@keyframes redeemGlowPulse {
  0%, 100% {
    opacity: .52;
    transform: scale(.88);
  }
  50% {
    opacity: 1;
    transform: scale(1.12);
  }
}

@keyframes redeemStatusPulse {
  0%, 100% {
    opacity: .5;
    transform: scale(.82);
  }
  50% {
    opacity: 1;
    transform: scale(1.18);
  }
}

@keyframes redeemPeriodShine {
  0%, 18% {
    left: -45px;
    opacity: 0;
  }
  30% { opacity: 1; }
  52% {
    left: calc(100% + 45px);
    opacity: 0;
  }
  100% {
    left: calc(100% + 45px);
    opacity: 0;
  }
}

@keyframes redeemTimelineFlow {
  from { transform: translateX(-100%); }
  to { transform: translateX(180%); }
}

@keyframes redeemButtonShine {
  0%, 20% {
    left: -46px;
    opacity: 0;
  }
  32% { opacity: 1; }
  54% {
    left: calc(100% + 46px);
    opacity: 0;
  }
  100% {
    left: calc(100% + 46px);
    opacity: 0;
  }
}

@media (max-width: 380px) {
  .redeem-header {
    min-height: 70px;
    padding-right: 13px;
    padding-left: 13px;
  }

  .header-copy h2 {
    font-size: 18px;
  }

  .redeem-body {
    padding-right: 9px;
    padding-left: 9px;
  }

  .order-summary,
  .redeem-section {
    padding-right: 12px;
    padding-left: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .redeem-grid-bg,
  .redeem-scan-beam,
  .redeem-aurora,
  .redeem-particles i,
  .redeem-header::after,
  .order-summary::before,
  .summary-glow,
  .status-badge i,
  .period-card::before,
  .timeline-line::after,
  .confirm-btn::before {
    animation: none !important;
  }
}

</style>
